import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './login.module.scss';
import { validateLoginIdentifier, validatePasswordField } from 'utils/validation';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { loginUser, clearError, verify2FA, setRequiresTwoFactor } from 'store/authSlice';
import { Alert } from 'components/alert';
import { authService } from 'api/auth';
import { ActionButton } from 'components/actionButton/actionButton';
import { showToast } from 'store/toastSlice';
import { EyeIcon, EyeOffIcon } from 'components/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import { getAndClearPendingGameRedirect } from 'utils/pendingRedirect';
import { getLocalizedPath, getUnlocalizedPath } from 'utils/localizedRouting';

const LazyGoogleButton = lazy(() => import('components/GoogleButton/GoogleButton'));

interface LoginProps {
  onClose: () => void;
  onRegisterClick?: () => void;
  onLoginSuccess?: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

const Login: React.FC<LoginProps> = ({
  onClose,
  onRegisterClick,
  onLoginSuccess,
  onLoadingChange,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useLocalizedNavigate();
  const rawNavigate = useNavigate();
  const location = useLocation();
  const {
    isLoading,
    error: serverError,
    isAuthenticated,
    requiresTwoFactor,
    twoFactorToken,
    user,
  } = useAppSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    twoFactorCode: '',
  });

  const [currentView, setCurrentView] = useState<'login' | 'forgotPassword'>('login');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
  const [isForgotPasswordLoading, setIsForgotPasswordLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const config = useAppSelector((state) => state.site.config);
  const isBlocked = config?.geo?.isBlocked;

  const isAnyLoading = isLoading || isGoogleLoading;
  const isFormDisabled = isAnyLoading || isBlocked;
  const isForgotPasswordFormDisabled = isForgotPasswordLoading || isBlocked;

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isGoogleLoading);
    }
  }, [isGoogleLoading, onLoadingChange]);

  const hasHandledAuthSuccessRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !hasHandledAuthSuccessRef.current) {
      hasHandledAuthSuccessRef.current = true;

      const userLang = user?.language || user?.profile?.language;
      const storedLang = typeof window !== 'undefined' ? localStorage.getItem('language') : null;
      const targetLang = userLang || storedLang || 'en';

      if (typeof window !== 'undefined') {
        localStorage.setItem('language', targetLang);
      }

      const showLoginToast = async () => {
        let toastText = t('auth.loginSuccess', 'You have successfully logged in.');

        if (targetLang !== i18n.language) {
          try {
            await i18n.changeLanguage(targetLang);
            toastText = i18n.t('auth.loginSuccess', {
              lng: targetLang,
              defaultValue: 'You have successfully logged in.',
            });
          } catch (error) {
            console.error('Failed to change language for login toast:', error);
          }
        }

        dispatch(showToast({ text: toastText, type: 'success' }));
      };

      showLoginToast();

      const pendingRedirect = getAndClearPendingGameRedirect();
      if (pendingRedirect) {
        onClose();
        navigate(pendingRedirect.path, { state: pendingRedirect.state });
      } else {
        onClose();
        const currentUnlocalized = getUnlocalizedPath(location.pathname);
        const targetPath = getLocalizedPath(
          currentUnlocalized + location.search + location.hash,
          targetLang,
        );
        rawNavigate(targetPath, { replace: true, state: location.state });
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      }
    }
  }, [
    isAuthenticated,
    user,
    onLoginSuccess,
    onClose,
    dispatch,
    navigate,
    rawNavigate,
    location,
    t,
    i18n,
  ]);

  useEffect(() => {
    if (serverError) {
      dispatch(clearError());
    }
  }, [formData.email, formData.password, twoFactorCode, dispatch, serverError]);

  const handleLogin = () => {
    let isValid = true;
    const newErrors = { email: '', password: '', twoFactorCode: '' };

    const emailError = validateLoginIdentifier(formData.email);
    if (emailError) {
      newErrors.email = emailError;
      isValid = false;
    }

    const passwordError = validatePasswordField(formData.password);
    if (passwordError) {
      newErrors.password = passwordError;
      isValid = false;
    }

    setErrors(newErrors);

    if (isValid) {
      dispatch(
        loginUser({
          login: formData.email,
          password: formData.password,
        }),
      );
    }
  };

  const handleVerify2FA = () => {
    if (twoFactorCode.length < 6) {
      setErrors({
        ...errors,
        twoFactorCode: t('auth.enterSixDigitCode', 'Please enter a 6-digit code'),
      });
      return;
    }

    if (twoFactorToken) {
      dispatch(
        verify2FA({
          code: twoFactorCode,
          twoFactorToken: twoFactorToken,
        }),
      );
    }
  };

  const handleBackToLogin = () => {
    dispatch(setRequiresTwoFactor(false));
    setTwoFactorCode('');
  };

  const handleForgotPassword = async () => {
    const emailError = validateLoginIdentifier(forgotPasswordEmail);
    if (emailError) {
      setForgotPasswordError(emailError);
      return;
    }

    setIsForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    try {
      const response = await authService.forgotPassword(forgotPasswordEmail);
      setForgotPasswordSuccess(
        response.data?.message ||
          t('auth.resetLinkSent', 'Password reset link sent to your email.'),
      );
    } catch (err: any) {
      setForgotPasswordError(
        err.response?.data?.message ||
          err.message ||
          t('auth.resetLinkFailed', 'Failed to send reset link.'),
      );
    } finally {
      setIsForgotPasswordLoading(false);
    }
  };

  return (
    <>
      {requiresTwoFactor ? (
        <div className={styles.twoFactorView}>
          <h2 className={styles.viewTitle}>
            {t('auth.twoFactorTitle', 'Two-Factor Authentication')}
          </h2>
          <p className={styles.viewDescription}>
            {t(
              'auth.twoFactorDescription',
              'Enter the 6-digit verification code from your authenticator app.',
            )}
          </p>

          <div className={styles.inputGroup}>
            <label htmlFor="twoFactorCode">
              {t('auth.verificationCode', 'Verification Code')}{' '}
              <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="twoFactorCode"
              className={`${styles.inputField} ${errors.twoFactorCode ? styles.inputError : ''}`}
              value={twoFactorCode}
              placeholder="XXXXXX"
              maxLength={6}
              onChange={(e) => {
                setTwoFactorCode(e.target.value);
                if (errors.twoFactorCode) setErrors({ ...errors, twoFactorCode: '' });
              }}
              disabled={isFormDisabled}
            />
            {errors.twoFactorCode && (
              <span className={styles.errorMsg}>{errors.twoFactorCode}</span>
            )}
          </div>

          <Alert message={serverError || undefined} type="error" />

          <button className={styles.submitBtn} onClick={handleVerify2FA} disabled={isFormDisabled}>
            {isAnyLoading
              ? t('auth.verifying', 'Verifying...')
              : t('auth.verifyAndLogin', 'Verify & Login')}
          </button>

          <button className={styles.backBtn} onClick={handleBackToLogin} disabled={isFormDisabled}>
            {t('auth.backToLogin', 'Back to Login')}
          </button>
        </div>
      ) : currentView === 'forgotPassword' ? (
        <div className={styles.twoFactorView}>
          <h2 className={styles.viewTitle} style={{ marginBottom: '10px' }}>
            {t('auth.resetPasswordTitle', 'Reset Password')}
          </h2>
          <p
            className={styles.viewDescription}
            style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px' }}
          >
            {t(
              'auth.resetPasswordDescription',
              'Enter your email address and we will send you a link to reset your password.',
            )}
          </p>

          <div className={styles.inputGroup}>
            <label htmlFor="forgotEmail">
              {t('common.email', 'Email Address')} <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="forgotEmail"
              className={`${styles.inputField} ${forgotPasswordError ? styles.inputError : ''}`}
              value={forgotPasswordEmail}
              onChange={(e) => {
                setForgotPasswordEmail(e.target.value);
                if (forgotPasswordError) setForgotPasswordError('');
                if (forgotPasswordSuccess) setForgotPasswordSuccess('');
              }}
              disabled={isForgotPasswordFormDisabled}
            />
          </div>

          <Alert
            message={forgotPasswordError || forgotPasswordSuccess || undefined}
            type={forgotPasswordError ? 'error' : 'success'}
          />

          <button
            className={styles.submitBtn}
            onClick={handleForgotPassword}
            disabled={isForgotPasswordFormDisabled}
            style={{ marginTop: '16px' }}
          >
            {isForgotPasswordLoading
              ? t('auth.sending', 'Sending...')
              : t('auth.sendResetLink', 'Send Reset Link')}
          </button>

          <button
            className={styles.backBtn}
            onClick={() => {
              if (isForgotPasswordLoading) return;
              setCurrentView('login');
              setForgotPasswordError('');
              setForgotPasswordSuccess('');
            }}
            disabled={isForgotPasswordFormDisabled}
          >
            {t('auth.backToLogin', 'Back to Login')}
          </button>
        </div>
      ) : (
        <>
          <div className={styles.inputGroup}>
            <label htmlFor="email">
              {t('auth.emailOrUsername', 'Email or Username')}{' '}
              <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="email"
              className={`${styles.inputField} ${errors.email ? styles.inputError : ''}`}
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (errors.email) setErrors({ ...errors, email: '' });
              }}
              disabled={isFormDisabled}
            />
            {errors.email && <span className={styles.errorMsg}>{errors.email}</span>}
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="password">
              {t('common.password', 'Password')} <span className={styles.required}>*</span>
            </label>
            <div className={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className={`${styles.inputField} ${errors.password ? styles.inputError : ''}`}
                value={formData.password}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  if (errors.password) setErrors({ ...errors, password: '' });
                }}
                disabled={isFormDisabled}
              />
              <button
                type="button"
                className={styles.eyeBtn}
                onClick={() => !isFormDisabled && setShowPassword(!showPassword)}
                aria-label="Toggle password visibility"
                disabled={isFormDisabled}
              >
                {showPassword ? (
                  <EyeIcon width="20" height="20" />
                ) : (
                  <EyeOffIcon width="20" height="20" />
                )}
              </button>
            </div>
            {errors.password && <span className={styles.errorMsg}>{errors.password}</span>}
          </div>

          <div className={styles.forgotPassword}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (isFormDisabled) return;
                setCurrentView('forgotPassword');
              }}
              className={isFormDisabled ? styles.disabledLink : ''}
            >
              {t('auth.forgotPassword', 'Forgot Password?')}
            </a>
          </div>

          <Alert message={serverError || undefined} type="error" />

          <ActionButton
            className={styles.submitBtn}
            onClick={handleLogin}
            disabled={isFormDisabled}
            isLoading={isAnyLoading}
          >
            {t('common.login', 'Login')}
          </ActionButton>

          <div className={styles.divider}>
            <span>{t('common.or', 'OR')}</span>
          </div>

          <Suspense
            fallback={
              <button className={styles.googleBtn} disabled>
                {isFormDisabled
                  ? t('auth.loginWithGoogle', 'Login with Google')
                  : t('auth.loadingGoogle', 'Loading Google Auth...')}
              </button>
            }
          >
            <LazyGoogleButton
              disabled={isFormDisabled}
              setIsGoogleLoading={setIsGoogleLoading}
              text={t('auth.loginWithGoogle', 'Login with Google')}
            />
          </Suspense>

          <div className={styles.registerPrompt}>
            {t('auth.dontHaveAccount', "Don't have an account?")}{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (isFormDisabled) return;
                if (onRegisterClick) {
                  onClose();
                  onRegisterClick();
                }
              }}
              className={isAnyLoading ? styles.disabledLink : ''}
            >
              {t('auth.registerAnAccount', 'Register an Account')}
            </a>
          </div>
        </>
      )}
    </>
  );
};

export default Login;
